import 'vue-loading-overlay/dist/vue-loading.css';
import axios from "axios";
import 'flatpickr/dist/flatpickr.css';
import CONFIG from "@/assets/js/config.custom.js";
import EnrollmentPaymentCreate from '@/controllers/custom/payment/enrollment-payment-create.js';

export default {
    ...EnrollmentPaymentCreate,
    name: 'enrollmentPaymentEdit',
    data() {
        return {
            ...EnrollmentPaymentCreate.data(),
            noDataMsg: CONFIG.NO_DATA_EXIST,
            dataNotFound: false,
        };
    },

    computed: {
        ...EnrollmentPaymentCreate.computed,
        hasEnrollmentData() {
            return !this.dataNotFound
                && this.studentInfo
                && this.studentInfo.student_id != null;
        },
    },

    watch: {
        ...EnrollmentPaymentCreate.watch,
        '$route.params.tuitionPaymentId'(newId, oldId) {
            if (oldId !== undefined && newId !== oldId) {
                this.loadEnrollmentEdit();
            }
        },
        '$route.params.classStudentId'(newId, oldId) {
            if (oldId !== undefined && newId !== oldId) {
                this.loadEnrollmentEdit();
            }
        },
    },

    methods: {
        ...EnrollmentPaymentCreate.methods,
        /**
         * Redirect to statement list
         */
        showStatementList() {
            this.$router.push({
                path: "/student/statement/list"
            });
        },

        /**
         * Redirect to statement detail
         */
        showStatementDetail() {
            this.$router.push({
                name: "studentStatementDetail",
                params: {
                    tuitionPaymentId: this.$route.params.tuitionPaymentId,
                }
            });
        },

        markDataNotFound() {
            this.studentInfo = {};
            this.specialDiscounts = [];
            this.tuitionPaymentPlans = [];
            this.dataNotFound = true;
            this.isLoading = false;
        },

        loadEnrollmentEdit() {
            this.isLoading = true;
            this.dataNotFound = false;
            this.studentInfo = {};
            this.specialDiscounts = [];
            this.tuitionPaymentPlans = [];
            this.tuitionPaymentId = this.$route.params.tuitionPaymentId;
            return this.getStudentInfo().then(() => {
                if (!this.dataNotFound) {
                    this.getDiscountTypes();
                }
            });
        },

        /**
         * Get student info; show no-data when student or tuition plan is missing
         */
        getStudentInfo() {
            return axios.get(`enrollment/student/info/${this.$route.params.classStudentId}`)
                .then(async response => {
                    const info = response.data;
                    if (!info || info.student_id == null) {
                        this.markDataNotFound();
                        return;
                    }
                    this.studentInfo = info;
                    this.specialDiscounts = this.applyLifetimeDiscountRules(
                        this.normalizeDiscountRows(response.data.special_discounts || [])
                    );
                    this.syncDiscountLimitValidation();

                    const hasPlan = await this.fetchAndApplyTuitionPlan();
                    if (!hasPlan) {
                        this.markDataNotFound();
                        return;
                    }

                    if (!Array.isArray(this.academicYearOptions) || this.academicYearOptions.length === 0) {
                        await this.initAcademicYearOptions();
                    }
                })
                .catch(error => {
                    if (error.response?.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                        return;
                    }
                    console.log("Error fetching student info:", error);
                    this.markDataNotFound();
                });
        },

        /**
         * Load tuition plan for edit; returns false when plan does not exist
         * @returns {Promise<boolean>}
         */
        fetchAndApplyTuitionPlan() {
            const rawId = this.tuitionPaymentId;
            const numId = Number(rawId);
            if (!rawId || Number.isNaN(numId)) {
                return Promise.resolve(false);
            }
            return axios.get(`enrollment/student/tuition-plan/${rawId}`)
                .then(async response => {
                    const tuitionPayment = response.data;
                    if (
                        !tuitionPayment ||
                        (Array.isArray(tuitionPayment.tuition_payment) &&
                            tuitionPayment.tuition_payment.length === 0)
                    ) {
                        return false;
                    }
                    this.applyingTuitionPaymentRestore = true;
                    this.firstAttendanceDate = tuitionPayment.first_attendance_date;
                    this.firstCalculationDate = tuitionPayment.first_calculation_date;
                    this.originalTuitionFee = tuitionPayment.original_tuition_fee;
                    this.proratedTuitionFee = tuitionPayment.prorated_tuition_fee;
                    this.finalTuitionFee = tuitionPayment.final_tuition_fee;
                    this.specialDiscounts = this.applyLifetimeDiscountRules(
                        this.normalizeDiscountRows(tuitionPayment.tuition_discounts || [])
                    );
                    this.syncDiscountLimitValidation();
                    this.tuitionPaymentPlans = tuitionPayment.payment_plans || [];
                    this.paymentPlanId = tuitionPayment.payment_plan_id;
                    this.formattedFinalTuitionFee = tuitionPayment.formatted_final_tuition_fee || 0;
                    const parentId = tuitionPayment?.acd_year_id != null ? Number(tuitionPayment.acd_year_id) : null;
                    const currentId = this.studentInfo?.active_acdemic_year_id != null ? Number(this.studentInfo.active_acdemic_year_id) : null;
                    const shouldForceShowCurrentYear =
                        parentId != null &&
                        currentId != null &&
                        parentId === currentId &&
                        this.studentInfo?.has_tuition_for_current_acd_year;
                    if (shouldForceShowCurrentYear) {
                        await this.initAcademicYearOptions({ forceShowCurrentYear: true });
                    } else {
                        await this.initAcademicYearOptions();
                        if (parentId) {
                            this.selectedAcdYearId = parentId;
                            await this.loadClassListForTerm(parentId);
                            this.selectedNextYearClassId = tuitionPayment.class_id;
                        }
                    }
                    this.calculateProratedTuitionFee();
                    this.$nextTick(() => {
                        this.applyingTuitionPaymentRestore = false;
                    });
                    return true;
                })
                .catch(error => {
                    this.applyingTuitionPaymentRestore = false;
                    console.log("Error fetching tuition plan:", error);
                    return false;
                });
        },

        /**
         * Override saveOrUpdateTuitionPayment to handle edit-specific redirection
         * @param {Object} data 
         */
        saveOrUpdateTuitionPayment(data) {
            axios.post(`enrollment/update/tuition-plan/${this.tuitionPaymentId}/${this.$route.params.classStudentId}`, data)
                .then(response => {
                    this.isLoading = false;
                    
                    if (response.status != 200) {
                        this.$notification.error(response.data.message, {
                            timer: 3,
                            position: "bottomCenter",
                        });
                        return;
                    }

                    this.$notification.success("Tuition plan updated successfully.", {
                        timer: 3,
                        position: "bottomCenter",
                    });
                    this.$router.push({
                        name: "enrollmentInvoiceEdit",
                        params: {
                            classStudentId: this.$route.params.classStudentId,
                            tuitionPaymentId: this.tuitionPaymentId,
                        }
                    });
                })
                .catch(error => {
                    this.isLoading = false;
                    if (error.response?.status == 422) {
                        this.printValidationError(error.response);
                        const errors = error.response.data.errors || {};
                        if (errors.invalid_tuition_payment_id) {
                          this.$notification.error(errors.invalid_tuition_payment_id[0], {
                            timer: 3,
                            position: "bottomCenter",
                          });
                        }
                    } else if (error.response?.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    } else {
                        this.$notification.error(error.response?.data?.error || "An error occurred.", {
                            timer: 3,
                            position: "bottomCenter",
                        });
                    }
                    console.log("Error updating tuition plan:", error);
                });
        },
    },

    async mounted() {
        await this.loadEnrollmentEdit();
    }
};
